<template>
  <template v-if="model?.typeDemande === 'ReservationFonds'">
    <BasicText>Conditions de réservation d’une DRF :</BasicText>
    <BasicText>
      <ol class="introduction">
        <li class="body-l regular">Délai de réponse : AKTO répond dans un délai de 48 heures ouvrées.</li>
        <li class="body-l regular">
          Éligibilité des contrats : La réservation de financement ne peut être réalisée que pour des contrats qui n’ont
          pas démarré à la date de réception de la demande par AKTO. Comme une DRF peut être refusée, l’ETT doit
          attendre la validation de sa DRF par AKTO avant de débuter ses contrats. Toute demande de réservation
          concernant des contrats déjà commencés sera automatiquement refusée.
        </li>
        <li class="body-l regular">
          Acceptation des contrats : La réservation de financement pour le projet décrit ne vaut pas acceptation
          automatique des contrats qui seront déposés. Ces contrats seront examinés par AKTO.
        </li>
        <li class="body-l regular">
          Délais à respecter : Les contrats liés à la demande doivent démarrer et être envoyés à AKTO dans un délai
          maximum de 30 jours calendaires après la réservation de financement par AKTO (hors projet HOPE, hors PIC IAE).
          Passé ce délai, si les contrats ne sont pas parvenus à AKTO, les fonds réservés seront réaffectés à d’autres
          projets. Pour les contrats envoyés hors délai et dont la DRF a été annulée, aucune prise en charge par AKTO ne
          sera possible.
        </li>
      </ol>
    </BasicText>
  </template>
  <template v-else>
    <template v-if="model?.sousDispositif === 'CIPI'">
      <BasicText>
        Le contrat d’insertion professionnelle intérimaire (CIPI) est un dispositif spécifique de la Branche du travail
        temporaire, visant le développement des compétences des salariés intérimaires, qu’ils soient en contrat de
        travail temporaire (CTT) ou en CDI Intérimaires (CDII),
      </BasicText>
      <BasicText>
        C’est un dispositif du Fonds professionnel pour l’emploi dans le travail temporaire (FPETT) dont la gestion
        administrative est assurée par AKTO.
      </BasicText>
      <BasicText>
        Avant de conclure un contrat d’insertion professionnelle intérimaire (CIPI), n’hésitez pas à vous rapprocher de
        votre conseiller pour vous renseigner sur les règles applicables.
      </BasicText>
      <BasicText>
        Pour vous aider dans la compréhension du dispositif et la saisie de la demande, vous trouverez ci-dessous des
        liens utiles.
      </BasicText>
    </template>
    <template v-if="model?.sousDispositif === 'CDPI'">
      <BasicText>
        Le contrat de développement professionnel intérimaire (CDPI) est un dispositif spécifique de la Branche du
        travail temporaire, visant le développement des compétences des salariés intérimaires, qu’ils soient en contrat
        de travail temporaire (CTT) ou en CDI Intérimaires (CDII),
      </BasicText>
      <BasicText>
        C’est un dispositif du Fonds professionnel pour l’emploi dans le travail temporaire (FPETT) dont la gestion
        administrative est assurée par AKTO.
      </BasicText>
      <BasicText>
        Avant de conclure un contrat de développement professionnel intérimaire (CDPI), n’hésitez pas à vous rapprocher
        de votre conseiller pour vous renseigner sur les règles applicables.
      </BasicText>
      <BasicText>
        Pour vous aider dans la compréhension du dispositif et la saisie de la demande, vous trouverez ci-dessous des
        liens utiles.
      </BasicText>
    </template>
  </template>

  <BasicText>
    <ul>
      <li
        v-for="(urlLabel, i) in urlLabels"
        :key="i"
      >
        <BasicLink
          :label="urlLabel.label"
          @link-click="redirectExterne(urlLabel.url)"
        />
      </li>
    </ul>
  </BasicText>

  <WrapperCheckbox
    v-model="validationNotice"
    :alertLabel="meta.validationNotice?.$messageErreurDirty!"
    :readonly
  >
    <BasicText v-if="estDossier">
      J'ai pris connaissance de la
      <BasicLink
        label="notice"
        @link-click="redirectExterne(urlNotice)"
      />
      comprenant les informations liées au traitement des données à caractère personnel.
    </BasicText>
    <BasicText v-else>J’atteste avoir pris connaissance des conditions de réservation d’une DRF.</BasicText>
  </WrapperCheckbox>
</template>

<script setup lang="ts">
  import { DEMANDE_STORE } from '@/features/interface/demande/shared'
  import type { UrlLabel } from '@/features/interface/shared/tools'
  import { redirectExterne } from '@/features/shared/tools/url'
  import { storeToRefs } from 'pinia'
  import { computed, inject } from 'vue'

  const store = inject(DEMANDE_STORE)!.getStore<'MODEL_CXPI'>()
  const { meta, readonly, estDossier, model } = storeToRefs(store)
  const { setupFieldWithKey } = store
  const validationNotice = setupFieldWithKey<boolean>('validationNotice')

  const urlLabels = computed(() => {
    const urlsLabels: UrlLabel[] = []
    if (model.value?.typeDemande === 'Formation') {
      urlsLabels.push({
        label: 'Les règles de prise en charge',
        url: 'https://www.akto.fr/regles-de-prise-en-charge-travail-temporaire/)',
      })
      urlsLabels.push({
        label:
          model.value?.sousDispositif === 'CIPI'
            ? 'Contrat d’insertion professionnelle intérimaire site AKTO'
            : 'Contrat de développement professionnel intérimaire site AKTO',
        url:
          model.value?.sousDispositif === 'CIPI'
            ? 'https://www.akto.fr/contrat-insertion-professionnelle-interimaire'
            : 'https://www.akto.fr/contrat-developpement-professionnel-interimaire/',
      })

      urlsLabels.push({
        label:
          model.value?.sousDispositif === 'CIPI'
            ? 'Contrat d’insertion professionnelle intérimaire site FPETT'
            : 'Contrat de développement professionnel intérimaire site FPETT',
        url:
          model.value?.sousDispositif === 'CIPI'
            ? 'https://www.fpett.fr/le-cipi-en-un-coup-doeil'
            : 'https://www.fpett.fr/contrat-de-developpement-professionnel-interimaire-cdpi',
      })
      urlsLabels.push({
        label: 'Le mode opératoire',
        url:
          model.value?.sousDispositif === 'CIPI'
            ? 'https://ressourcesstatiques.akto.fr/guide/v2/GuideDrfCipi.pdf'
            : 'https://ressourcesstatiques.akto.fr/guide/v2/GuideDrfCdpi.pdf',
      })
    }

    return urlsLabels
  })

  const urlNotice = computed(() => {
    const urlResource = import.meta.env.VITE_RESSOURCES_STATIQUES
    return urlResource + '/notice/NoticeCipiCdpiCar.pdf'
  })
</script>

<style scoped lang="less">
  .introduction li {
    line-height: 1.5rem;
  }
</style>
